<template>
  <div>
    <div class="row mb-4">
      <div class="col-12 col-md-8 offset-md-2">
        <input-field
            v-model="taskSearchQuery"
            :withSuffix="true"
            placeholder="Search by task name"
        />
      </div>
    </div>

    <div class="row my-4">
      <div class="col">
        <div class="filter-title">
          <span v-if="startDate === endDate">Today</span>
          <span class="filter-subtitle" v-else>{{ formatDateForDisplay(this.startDate) }}</span>
          <span class="filter-subtitle">- {{ formatDateForDisplay(this.endDate) }}</span>
        </div>
      </div>

      <div class="col col-auto d-flex">
        <button type="button" class="basic-button" @click="showFilterModal = !showFilterModal">
          <FilterIcon/>
          Filters
        </button>
      </div>
    </div>

    <div class="row mb-4">
      <div class="col-12">
        <staff-task-item v-for="task of tasks" :task="task" :key="task.id"/>

        <div v-if="tasks.length === 0" class="text-center font-weight-bold">
          No tasks found. Try selecting different date range.
        </div>
      </div>
    </div>

    <div class="row" v-if="hasMoreTasks">
      <div class="col-12 col-sm-5 mx-auto">
        <button
            @click="fetchMoreTasks"
            type="button"
            class="wfh-btn-color-primary wfh-btn-large btn-block"
        >
          See More
        </button>
      </div>
    </div>

    <transition name="fade">
      <date-filter-modal
          v-if="showFilterModal"
          @close="showFilterModal = false"
          @selected="dateSelected"
      ></date-filter-modal>
    </transition>
  </div>
</template>

<script>
import FilterIcon from '@icons/FilterIcon';
import { debounce } from 'lodash';
import { getUserData } from '../../../../repositories/UserDataPersistence';
import minutesToHoursFormatter from '../../../../utils/minutesToHoursFormatter';

const STATUSES = {
  'completed': 'completed_at',
  'incomplete': 'pending',
};

export default {
  props: {
    status: {
      required: false,
      default: '',
    },
  },

  components: { FilterIcon },

  data () {
    return {
      userData: getUserData(),
      taskSearchQuery: null,
      tasks: [],
      showFilterModal: false,
      loading: true,
      companyId: null,
      startDate: null,
      endDate: null,
      currentPage: 1,
      totalPages: 1,
      reset: false,
    };
  },

  computed: {
    hasMoreTasks () {
      return this.loading === false && this.currentPage < this.totalPages;
    },
  },

  watch: {
    taskSearchQuery () {
      this.reset = true;
      this.currentPage = 1;
      this.totalPages = 1;
      this.debouncedFetch();
    },
  },

  methods: {
    minutesToHoursFormatter,

    debouncedFetch: debounce(function () {
      this.fetchTasks();
    }, 400),

    fetchMoreTasks () {
      this.currentPage++;
      this.fetchTasks();
    },

    async fetchTasks () {
      this.loading = true;
      const params = {
        is_personal: true,
        start_date: this.startDate,
        end_date: this.endDate,
        per_page: 10,
        page: this.currentPage,
        user_id: this.userData.id,
        company_id: this.userData.company.id,
      };

      if (this.status !== null) {
        params['status'] = STATUSES[this.status];
      }

      if (this.taskSearchQuery) {
        params['search_query'] = this.taskSearchQuery;
      }

      const res = await axios.get('/reports/tasks', { params });
      const tasks = res.data.data.map((task) => {
        return {
          date: moment(task.completed_at ?? task.combined_created_at).format('DD MMM YYYY'),
          id_hash: task.id_hash,
          id: task.id,
          title: task.title,
          assignee: task.users,
          totalTime: this.minutesToHoursFormatter(task.total_minutes_spent),
          status: !task.completed && task.total_minutes_spent > 0 ? 'on-going' : task.completed ? 'completed' : 'pending',
          detailsPageUrl: route('team-members.report.task-details', {
            hashedTaskId: task.id_hash,
            start_date: this.startDate,
            end_date: this.endDate,
          }),
          totalScreenshots: task.total_screenshots ?? 0,
        };
      });

      if (this.reset === true) {
        this.tasks = tasks;
      } else {
        this.tasks = [
          ...this.tasks,
          ...tasks,
        ];
      }

      this.totalPages = res.data.last_page;
      this.loading = false;
      this.reset = false;
    },

    dateSelected (startDate, endDate) {
      this.startDate = startDate;
      this.endDate = endDate;
      this.reset = true;

      this.fetchTasks();
      this.showFilterModal = false;
    },

    setDefaultDateRange () {
      this.startDate = localStorage.getItem('wfh_tm_sd') ?? moment().startOf('month').format('YYYY-MM-DD');
      this.endDate = localStorage.getItem('wfh_tm_ed') ?? moment().format('YYYY-MM-DD');
    },

    formatDateForDisplay (date) {
      return moment(date).format('DD/MM/YYYY');
    },
  },

  mounted () {
    this.setDefaultDateRange();
    this.fetchTasks();
  },
};
</script>
